import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { isToday, isYesterday, format } from "date-fns";
import { tr, enUS, de, ar, es, fr, it, type Locale } from "date-fns/locale";
import {
  ArrowLeft,
  ChevronUp,
  CheckCircle2,
  LockKeyhole,
  X,
} from "lucide-react";

import { getFileUrl } from "@/lib/utils";
import useAuthStore from "@/store/use-auth-store";
import { useToast } from "@/features/toast";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { useTicketDetail } from "../hooks/use-tickets";
import { TicketDetailHeader } from "./TicketDetailHeader";
import { TicketMessageBubble } from "./TicketMessageBubble";
import { TicketReplyComposer } from "./TicketReplyComposer";
import { isOwnTicketMessage } from "../utils/message-author";
import type { SupportTicketMessage } from "../types";

const DATE_LOCALES: Record<string, Locale> = { tr, en: enUS, de, ar, es, fr, it };

/** Aynı kişinin 5 dakika içindeki ardışık mesajları görsel olarak gruplanır */
const GROUPING_WINDOW_MS = 5 * 60 * 1000;

/**
 * Uzun konuşmalarda başlangıçta yalnızca son mesajlar basılır; kullanıcı
 * "önceki mesajlar" ile geçmişi kademeli açar. Backend tüm mesajları tek
 * payload'da döndürdüğü için sayfalama istemci tarafında yapılır.
 */
const INITIAL_VISIBLE_MESSAGES = 20;
const VISIBLE_MESSAGES_STEP = 20;

interface RenderedMessage {
  message: SupportTicketMessage;
  isOwn: boolean;
  isGrouped: boolean;
}

interface MessageGroup {
  dayKey: string;
  date: Date;
  messages: RenderedMessage[];
}

/** Mesajları güne göre bölüp aynı tarafın ardışık mesajlarını işaretler */
const groupMessages = (messages: RenderedMessage[]): MessageGroup[] => {
  const groups: MessageGroup[] = [];

  messages.forEach((entry, index) => {
    const date = new Date(entry.message.sentAt);
    const dayKey = format(date, "yyyy-MM-dd");
    const previous = messages[index - 1];

    const isGrouped =
      !!previous &&
      previous.isOwn === entry.isOwn &&
      format(new Date(previous.message.sentAt), "yyyy-MM-dd") === dayKey &&
      date.getTime() - new Date(previous.message.sentAt).getTime() <
        GROUPING_WINDOW_MS;

    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.dayKey === dayKey) {
      lastGroup.messages.push({ ...entry, isGrouped });
    } else {
      groups.push({
        dayKey,
        date,
        messages: [{ ...entry, isGrouped: false }],
      });
    }
  });

  return groups;
};

const TicketDetailSkeleton: React.FC = () => (
  <div className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
    <div className="flex flex-col gap-3 border-b border-border px-5 py-5 sm:px-6">
      <Skeleton className="h-5 w-16" />
      <Skeleton className="h-6 w-3/4" />
      <div className="flex gap-2">
        <Skeleton className="h-7 w-32" />
        <Skeleton className="h-7 w-28" />
      </div>
    </div>
    <div className="flex flex-col gap-6 px-5 py-6 sm:px-6">
      <div className="flex items-end gap-2.5">
        <Skeleton className="h-8 w-8 rounded-full" />
        <Skeleton className="h-16 w-2/3 rounded-2xl" />
      </div>
      <div className="flex flex-row-reverse items-end gap-2.5">
        <Skeleton className="h-8 w-8 rounded-full" />
        <Skeleton className="h-12 w-1/2 rounded-2xl" />
      </div>
    </div>
  </div>
);

/**
 * Ticket konuşma ekranı (tam sayfa) — başlık, mesaj thread'i ve cevap alanı.
 * Ticket id'si URL'den (/support/tickets/:id) okunur.
 */
export const TicketDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { error: toastError } = useToast();

  const threadRef = useRef<HTMLDivElement>(null);
  const previousMessageCount = useRef(0);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_MESSAGES);

  const { ticket, isLoading, isError, sendReply, isReplying } =
    useTicketDetail(id);

  const dateLocale =
    DATE_LOCALES[i18n.language?.split("-")[0] ?? "en"] ?? enUS;

  const messages = useMemo(() => ticket?.messages ?? [], [ticket?.messages]);

  const hiddenCount = Math.max(0, messages.length - visibleCount);
  const visibleMessages = useMemo(
    () => (hiddenCount > 0 ? messages.slice(hiddenCount) : messages),
    [messages, hiddenCount],
  );
  const groups = useMemo(
    () =>
      groupMessages(
        visibleMessages.map((message) => ({
          message,
          isOwn: isOwnTicketMessage(message, user),
          isGrouped: false,
        })),
      ),
    [visibleMessages, user],
  );

  // Başka bir ticket'a geçildiğinde pencere baştan başlar
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_MESSAGES);
    previousMessageCount.current = 0;
  }, [id]);

  // İlk açılışta anında en alta; sonraki mesajlarda yumuşak kaydırma
  useEffect(() => {
    const thread = threadRef.current;
    if (!thread || messages.length === 0) return;

    if (previousMessageCount.current === 0) {
      // Görseller yerleşene kadar bekle, aksi hâlde yükseklik sonradan büyür
      requestAnimationFrame(() => {
        thread.scrollTop = thread.scrollHeight;
      });
    } else if (messages.length > previousMessageCount.current) {
      thread.scrollTo({ top: thread.scrollHeight, behavior: "smooth" });
    }

    previousMessageCount.current = messages.length;
  }, [messages.length]);

  /** Geçmişi açarken okunan mesajın ekrandaki konumu korunur */
  const handleShowEarlier = () => {
    const thread = threadRef.current;
    const previousHeight = thread?.scrollHeight ?? 0;

    setVisibleCount((current) => current + VISIBLE_MESSAGES_STEP);

    requestAnimationFrame(() => {
      if (!thread) return;
      thread.scrollTop += thread.scrollHeight - previousHeight;
    });
  };

  // Lightbox açıkken Esc ile kapat
  useEffect(() => {
    if (!lightboxUrl) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLightboxUrl(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxUrl]);

  const isClosed = ticket?.status === "closed";
  const ticketModel = messages.find((message) => message.model)?.model;
  const userImageUrl = user?.image ? getFileUrl(user.image) : null;

  // Misafir (magic link) kullanıcı ticket listesine erişemez → forma döner
  const isLoggedIn = !!user?._id;
  const backLabel = isLoggedIn
    ? t("support.myTickets.backToList")
    : t("support.myTickets.backToSupport");
  // Admin "Tüm talepler" kapsamından geldiyse aynı sekmeye dönülür
  const scopeSuffix = searchParams.get("scope") === "all" ? "?scope=all" : "";
  const backPath = isLoggedIn ? `/support/tickets${scopeSuffix}` : "/support";

  const handleSend = async (payload: { body: string; images: File[] }) => {
    try {
      await sendReply(payload);
    } catch {
      toastError(t("support.myTickets.replyError"));
      throw new Error("reply-failed");
    }
  };

  const formatDayLabel = (date: Date): string => {
    if (isToday(date)) return t("support.myTickets.today");
    if (isYesterday(date)) return t("support.myTickets.yesterday");
    return format(date, "d MMMM yyyy", { locale: dateLocale });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-28" />
        <TicketDetailSkeleton />
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-border bg-card p-10 text-center shadow-sm">
        <p className="text-sm text-muted-foreground">
          {t("support.myTickets.loadError")}
        </p>
        <Button
          asChild
          variant="outline"
          size="sm"
          className="dark:border-border dark:bg-muted/50 dark:text-foreground dark:hover:bg-muted"
        >
          <Link to={backPath}>
            <ArrowLeft className="h-3.5 w-3.5" />
            {backLabel}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2.5 sm:gap-4">
      {/* Üst çubuk — aside'daki h2 başlıkla (h-8) aynı yükseklikte, kartlar hizalanır.
          Close ticket butonu backend hazır olunca geri eklenecek. */}
      <div className="flex h-7 shrink-0 items-center gap-3 sm:h-8">
        <Link
          to={backPath}
          className="group inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          {backLabel}
        </Link>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="shrink-0">
          <TicketDetailHeader
            ticket={ticket}
            model={ticketModel}
            dateLocale={dateLocale}
          />
        </div>

        {/* Mesaj thread'i — kartın tek kayan bölümü */}
        <div
          ref={threadRef}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-3 sm:px-6 sm:py-5"
        >
          {/* Geçmişi kademeli açan sayfalama */}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={handleShowEarlier}
              className="mx-auto mb-3 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-indigo-500/50 hover:text-foreground"
            >
              <ChevronUp className="h-3.5 w-3.5" />
              {t("support.myTickets.loadEarlier", { count: hiddenCount })}
            </button>
          )}

          {/* mt-auto: kısa konuşmalarda mesajlar alta yaslanır */}
          <div className="mt-auto flex flex-col">
            {groups.map((group) => (
              <div key={group.dayKey} className="flex flex-col">
                {/* Gün ayıracı */}
                <div className="my-3 flex items-center gap-3 first:mt-0">
                  <div className="h-px flex-1 bg-border" />
                  <span className="shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {formatDayLabel(group.date)}
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                {group.messages.map(({ message, isOwn, isGrouped }, index) => {
                  // Sonraki mesaj bu gruba bağlı değilse grup burada biter
                  const next = group.messages[index + 1];
                  const isGroupEnd = !next || !next.isGrouped;

                  return (
                    <TicketMessageBubble
                      key={message._id ?? `${group.dayKey}-${index}`}
                      message={message}
                      dateLocale={dateLocale}
                      isOwn={isOwn}
                      userImageUrl={userImageUrl}
                      isGrouped={isGrouped}
                      isGroupEnd={isGroupEnd}
                      onImageClick={setLightboxUrl}
                    />
                  );
                })}
              </div>
            ))}

            {/* Kapatılmış ticket için zaman çizelgesi işareti */}
            {isClosed && (
              <div className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                {t("support.myTickets.resolvedMarker")}
              </div>
            )}
          </div>
        </div>

        {/* Cevap alanı / kapalı bildirimi — kaydırma dışında sabit kalır */}
        <div className="shrink-0">
          {isClosed ? (
            <div className="flex items-center justify-center gap-2 border-t border-border bg-muted/30 px-5 py-4 text-sm text-muted-foreground dark:bg-muted/10">
              <LockKeyhole className="h-4 w-4" />
              {t("support.myTickets.closedNotice")}
            </div>
          ) : (
            <TicketReplyComposer isReplying={isReplying} onSend={handleSend} />
          )}
        </div>
      </div>

      {/* Görsel önizleme (lightbox)
          GlobalHeader z-[950], Dialog overlay z-[1100] — lightbox ikisinin
          üstünde olmalı, aksi halde header overlay'in üstünde kalıyor. */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setLightboxUrl(null)}
          role="presentation"
        >
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            aria-label={t("common.close")}
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightboxUrl}
            alt=""
            onClick={(event) => event.stopPropagation()}
            className="max-h-full max-w-full rounded-xl object-contain shadow-2xl"
          />
        </div>
      )}
    </div>
  );
};
